import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '~/@api/client/authStore'
import { useUser } from '~/@biz_account/useUser/useUser'
import { useLoginMutation, type LoginRequest } from './useLoginMutation'
import { useLoginTwoFaMutation } from './useLoginTwoFaMutation'
import { getApiErrorMessage } from '~/@lib/utils/error.utils'
import { useQueryClient } from '~/@mid_query/client/useQueryClient'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'

export function useLogin() {
  const queryClient = useQueryClient()
  const { clearUser } = useUser()
  const { setNotification } = useLocalNotifications()

  const [loginStep, setLoginStep] = useState<'credentials' | 'twoFa'>(
    'credentials'
  )
  const [loginCreds, setLoginCreds] = useState<LoginRequest | null>(null)
  const [twoFaSessionId, setTwoFaSessionId] = useState<string | null>(null)
  const [loginTwoFaCodeExpiresAt, setLoginTwoFaCodeExpiresAt] =
    useState<Date | null>(null)
  const { removeAuthToken, setAuthToken } = useAuthStore()
  const navigate = useNavigate()

  const loginMutation = useLoginMutation((data) => {
    if (data.twoFactorRequired) {
      setTwoFaSessionId(data.sessionId ?? null)
      setLoginTwoFaCodeExpiresAt(
        data.twoFaCodeExpiresAt ? new Date(data.twoFaCodeExpiresAt) : null
      )
      setLoginStep('twoFa')
      return
    }
    if (data.auth != null) {
      setAuthToken(data.auth)
      navigate('/')
      return
    }

    throw new Error('Unexpected response from server')
  })

  const loginTwoFaMutation = useLoginTwoFaMutation(
    twoFaSessionId ?? '',
    (data) => {
      if (data.auth != null) {
        setAuthToken(data.auth)
      }
    }
  )

  const handleReset = () => {
    setLoginCreds(null)
    loginMutation.handleReset()
    loginTwoFaMutation.handleReset()
    setTwoFaSessionId(null)
    setLoginStep('credentials')
  }

  function handleResendLoginTwoFaCode() {
    if (loginCreds == null) {
      // keby nahodou neboli k dispozicii loginCreds, tak reset a spat na credentials
      handleReset()
      setNotification('error', 'Opatovné zaslanie SMS sa nepodarilo.')
      return
    }

    loginMutation.loginUser(loginCreds, {
      onSuccess: () => {
        setNotification('success', 'Opätovné zaslanie SMS prebehlo úspešne.')
      },
    })
  }

  return {
    loginStep,

    loginCreds,
    setLoginCreds,

    loginTwoFaCodeExpiresAt,

    handleReset,
    handleResendLoginTwoFaCode,
    loginUserMutation: loginMutation.loginUser,
    isLoginRequesting: loginMutation.isRequesting,
    loginUserError: getApiErrorMessage(
      loginMutation.error,
      'Prihlásenie sa nepodarilo.'
    ),

    loginUserTwoFaMutation: loginTwoFaMutation.loginUserTwoFa,
    isLoginTwoFaRequesting: loginTwoFaMutation.isRequesting,
    loginUserTwoFaError: getApiErrorMessage(
      loginTwoFaMutation.error,
      'Kód sa nepodarilo overiť.'
    ),

    logoutUser: function () {
      queryClient.clear()
      removeAuthToken()
      clearUser()
    },
  }
}
